import { Fade, Stack, styled } from '@mui/material'
import { deleteNotification, postReadNotification } from 'api/v1/message'
import { slice } from 'lodash'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  useNotificationBadgeDisplay,
  useNotificationList,
} from 'store/app/hooks'
import { transientOptions } from '../../../styles/TransientOptions'
import { ReactComponent as SvgBell } from './bell.svg'
import { ButtonClear } from './ButtonClear'
import { NotificationList } from './List'
import { PopupTitle } from './Title'
import { updateNotificationAction } from '../../../store/app/actions/notificationActions'
import { themeValue } from 'styles'
import { NavIconButton } from '../styles'
import { MenuItem } from '../Menu/MenuItem'
import { MenuKeys, menuAtom } from '../Menu/atoms'
import { useSetAtom } from 'jotai'
import { MobileHead } from '../MobileHead'

const NoticeWrap = styled(Stack)`
  position: relative;
`

const Badge = styled('span', transientOptions)<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  position: absolute;
  right: 8px;
  top: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.notificationRed02};
  border: 2px solid ${({ theme }) => theme.color.bgNavButton};
`
const Popup = styled(Stack)`
  width: 340px;
  max-height: 327px;
  padding-bottom: 24px;
  border-radius: 0px 0px 6px 6px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: auto;
    max-height: unset;

    background-color: rgba(22, 25, 30, 1);
  }
`

const ModalHead = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  padding: 24px 24px 12px;
  color: ${({ theme }) => theme.color.primary30};
  cursor: pointer;
`

export function useNotificationPagination(limit: number = 10) {
  const dispatch = useDispatch()
  const [more, setMore] = useState(false)
  const notification = useNotificationList()

  const hasMore = more ? false : notification?.length > limit

  const clear = useCallback(() => {
    deleteNotification({ isAllRemove: true })
    setMore(false)
    dispatch(updateNotificationAction([]))
  }, [dispatch, setMore])

  const del = useCallback(
    (id: number) => {
      deleteNotification({ ids: [id] })
      const newList = notification.filter((item) => item.id !== id)
      dispatch(updateNotificationAction([...newList]))
    },
    [dispatch, notification],
  )
  const read = useCallback(() => {
    const ids = notification.filter((v) => !v.isRead).map((v) => v.id)
    postReadNotification(ids)
    dispatch(
      updateNotificationAction(
        notification.map((item) => ({ ...item, isRead: true })),
      ),
    )
  }, [dispatch, notification])
  const showMore = useCallback(() => {
    setMore(true)
  }, [setMore])

  const list = useMemo(() => {
    if (more) return notification

    return slice(notification, 0, limit)
  }, [limit, more, notification])
  return {
    list,
    more,
    clear,
    del,
    read,
    showMore,
    hasMore,
  }
}

export const NotificationView = memo(() => {
  const [isShowModal] = useState<boolean>(false)
  const isShowBadge = useNotificationBadgeDisplay()
  const setMenu = useSetAtom(menuAtom)

  return (
    <MenuItem
      menuKey={MenuKeys.Notification}
      popup={
        <Popup>
          <MobileHead onClose={() => setMenu(0)} />
          <ModalHead>
            <PopupTitle />
            <ButtonClear />
          </ModalHead>
          <NotificationList isShowModal={isShowModal} />
        </Popup>
      }
      direction="right"
      hideArrow={true}
      className="nav-item"
    >
      <NavIconButton className={isShowModal ? 'active' : ''}>
        <SvgBell />
        <Badge $visible={isShowBadge} />
      </NavIconButton>
    </MenuItem>
  )
})
